import React, { useState } from 'react';
import { FiFolder, FiFolderPlus, FiEdit2, FiTrash2, FiChevronRight, FiChevronDown } from 'react-icons/fi';

const FolderTree = ({ 
  folders = [], 
  selectedFolderId, 
  onFolderSelect, 
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  showActions = true 
}) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [parentFolderId, setParentFolderId] = useState(null);

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (newFolderName.trim()) {
      await onCreateFolder({
        name: newFolderName,
        parent_folder_id: parentFolderId
      });
      setNewFolderName('');
      setShowCreateForm(false);
      setParentFolderId(null);
    }
  };

  const renderFolder = (folder, level = 0) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolderId === folder.id;

    return (
      <div key={folder.id} className="folder-item">
        <div
          className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-colors ${
            isSelected
              ? 'bg-blue-100 text-blue-700'
              : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {/* Expand/Collapse Icon */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="mr-1 text-gray-500 hover:text-gray-700"
            >
              {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            </button>
          )}
          
          {/* Folder Icon */}
          <FiFolder 
            className={`mr-2 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}
            size={18}
          />
          
          {/* Folder Name */}
          <span
            onClick={() => onFolderSelect(folder.id)}
            className="flex-1 text-sm font-medium"
          >
            {folder.name}
          </span>

          {/* Document Count */}
          {folder.documentCount !== undefined && (
            <span className="text-xs text-gray-500 mr-2">
              ({folder.documentCount})
            </span>
          )}

          {/* Actions */}
          {showActions && (
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setParentFolderId(folder.id);
                  setShowCreateForm(true);
                }}
                className="p-1 text-gray-500 hover:text-blue-600 rounded"
                title="Add subfolder"
              >
                <FiFolderPlus size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEditFolder(folder);
                }}
                className="p-1 text-gray-500 hover:text-green-600 rounded"
                title="Edit folder"
              >
                <FiEdit2 size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteFolder(folder.id);
                }}
                className="p-1 text-gray-500 hover:text-red-600 rounded"
                title="Delete folder"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="folder-children">
            {folder.children.map(child => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="folder-tree bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Folders</h3>
        {showActions && (
          <button
            onClick={() => {
              setParentFolderId(null);
              setShowCreateForm(true);
            }}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Create root folder"
          >
            <FiFolderPlus size={18} />
          </button>
        )}
      </div>

      {/* Create Folder Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateFolder} className="mb-4 p-3 bg-gray-50 rounded-lg">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          <div className="flex justify-end space-x-2 mt-2">
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(false);
                setNewFolderName('');
                setParentFolderId(null);
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Create
            </button>
          </div>
        </form>
      )}

      {/* Root Folder */}
      <div
        className={`flex items-center py-2 px-3 rounded-lg cursor-pointer transition-colors mb-2 ${
          selectedFolderId === null
            ? 'bg-blue-100 text-blue-700'
            : 'hover:bg-gray-100'
        }`}
        onClick={() => onFolderSelect(null)}
      >
        <FiFolder className={`mr-2 ${selectedFolderId === null ? 'text-blue-600' : 'text-gray-500'}`} size={18} />
        <span className="text-sm font-medium">All Documents</span>
      </div>

      {/* Folder Tree */}
      <div className="space-y-1 group">
        {folders.map(folder => renderFolder(folder))}
      </div>

      {/* Empty State */}
      {folders.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <FiFolder size={48} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No folders yet</p>
          {showActions && (
            <button
              onClick={() => {
                setParentFolderId(null);
                setShowCreateForm(true);
              }}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              Create your first folder
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FolderTree;


